import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SquareImageComponent } from '~/components';
import {
  InfiniteScrollDropdownProps,
  InfiniteScrollDropdownSelection,
} from '~/components/InfiniteScrollDropdown/InfiniteScrollDropdown';
import { OrganizationPageQueryOrganization } from '~/constants/graphqlTypes';
import { OrganizationsPageQuery, OrganizationType, useOrganizationsPageQuery } from '~/types/api.graphql';
import { getOrganizationInitials } from '~/utils/organization';

import { DropdownItemProps } from '../Dropdown/Dropdown';
import { StyledInfiniteScrollDropdown } from './OrganizationsDropdown.styles';

const PAGE_ITEMS_COUNT = 10;

export interface OrganizationsDropdownProps
  extends Omit<InfiniteScrollDropdownProps<OrganizationPageQueryOrganization>, 'onSelectionChange'> {
  organizationType?: OrganizationType;
  isOrganizationDisabled?: (org: OrganizationPageQueryOrganization) => boolean;
  onSelectionChange?: (
    selection: InfiniteScrollDropdownSelection,
    organization: OrganizationPageQueryOrganization,
  ) => void;
  onAutoSelected?: (organization: OrganizationPageQueryOrganization) => void;
}

const OrganizationsDropdown = (props: OrganizationsDropdownProps) => {
  const {
    isOrganizationDisabled,
    onAutoSelected,
    onSelectionChange,
    organizationType,
    selectedIndex: selectedIndexProp,
  } = props;
  const { t } = useTranslation();
  const [items, setItems] = useState<DropdownItemProps<OrganizationPageQueryOrganization>[]>([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchOrgInfiniteScroll, setSearchOrgInfiniteScroll] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number>(props.selectedIndex);

  const {
    refetch,
    loading: loadingFirst,
    data: orgsQueryData,
  } = useOrganizationsPageQuery({
    variables: {
      pageNumber: page,
      pageItemsCount: PAGE_ITEMS_COUNT,
      searchQuery: searchOrgInfiniteScroll,
      organizationType,
    },
    fetchPolicy: 'network-only',
    skip: page > 1,
  });

  const disabledKeys = useMemo(() => {
    return items.filter((item) => item.disabled).map((item) => item.key);
  }, [items]);

  const applyOrgsPage = useCallback(
    (dataOrgs: OrganizationsPageQuery) => {
      setItems((prevItems) => [
        ...prevItems,
        ...(dataOrgs?.organizationsPage?.nodes?.map((organization) => ({
          text: organization.name,
          imgSrc: organization.imageUrl,
          value: organization,
          description: t('organization.settings.orgTypes.'.concat(String(organization.organizationType).toLowerCase())),
          key: organization.id,
          disabled: isOrganizationDisabled ? isOrganizationDisabled(organization) : false,
          icon: (
            <SquareImageComponent
              src={organization.imageUrl}
              squared
              text={getOrganizationInitials(organization.name || '')}
            />
          ),
        })) || []),
      ]);
      setHasMore(dataOrgs?.organizationsPage?.pageInfo?.hasNextPage);
      setPage((prevPage: number) => prevPage + 1);
    },
    [isOrganizationDisabled, t],
  );

  // Keep reset before the data effect so a search/org change clears stale rows
  // before the next page result is appended.
  useEffect(() => {
    setPage(1);
    setItems([]);
    setHasMore(false);
    setSelectedIndex(selectedIndexProp);
  }, [searchOrgInfiniteScroll, organizationType, selectedIndexProp]);

  useEffect(() => {
    if (orgsQueryData) {
      applyOrgsPage(orgsQueryData);
    }
    // Run once per Apollo data object. applyOrgsPage changes can re-append the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgsQueryData]);

  const handleItemSelection = useCallback(
    (selection) => {
      const selectedIndex = selection as number;
      setSelectedIndex(selectedIndex);
      onSelectionChange?.(selectedIndex, items[selectedIndex].value);
    },
    [items, onSelectionChange],
  );

  // Use a ref so the auto-selection effect only fires when items change,
  // not every time the parent re-renders and recreates onSelectionChange
  // (which would recreate handleItemSelection, causing an infinite render loop).
  const handleItemSelectionRef = useRef(handleItemSelection);
  handleItemSelectionRef.current = handleItemSelection;

  const onAutoSelectedRef = useRef(onAutoSelected);
  onAutoSelectedRef.current = onAutoSelected;

  useEffect(() => {
    if (items?.length === 1 && !items?.[0]?.disabled) {
      handleItemSelectionRef.current(0);
      onAutoSelectedRef.current?.(items[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const loadingFirstPage = loadingFirst && !items.length;

  const handleLoadMore = () => {
    if (!hasMore || loadingFirstPage || loadingMore) {
      return;
    }

    void (async () => {
      setLoadingMore(true);
      try {
        const result = await refetch({
          pageNumber: page,
          pageItemsCount: PAGE_ITEMS_COUNT,
          searchQuery: searchOrgInfiniteScroll,
          organizationType,
        });
        if (result.data) applyOrgsPage(result.data);
      } finally {
        setLoadingMore(false);
      }
    })();
  };

  return (
    <StyledInfiniteScrollDropdown
      {...props}
      items={items}
      disabledKeys={disabledKeys}
      onSelectionChange={handleItemSelection}
      selectedIndex={selectedIndex}
      setSearchOrgInfiniteScroll={setSearchOrgInfiniteScroll}
      searchOrgInfiniteScroll={searchOrgInfiniteScroll}
      hasMore={hasMore}
      loadMore={handleLoadMore}
      loadingFirst={loadingFirstPage}
      loadingMore={loadingMore}
      disallowEmptySelection
    />
  );
};

export default OrganizationsDropdown;
